import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { superadminAPI } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Shield,
  ArrowLeft,
  Plug,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  Loader2,
  AlertTriangle,
  Webhook,
  FileText,
  Mail,
  Users,
  Send,
  CreditCard,
  Key,
  Database,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { PageShell } from '@/components/PageShell';
import { Skeleton } from '@/components/ui/skeleton';
import IntegrationStatusPanel from '@/components/IntegrationStatusPanel';
import GCPInfrastructurePanel from '@/components/GCPInfrastructurePanel';

// Integration logos/icons
const INTEGRATION_ICONS = {
  stripe: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
    </svg>
  ),
  hubspot: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
      <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.066A2.198 2.198 0 0017.235.844h-.066a2.198 2.198 0 00-2.196 2.196v.066c0 .89.532 1.655 1.294 1.998v2.822a5.18 5.18 0 00-2.635 1.236l-7.067-5.5a2.504 2.504 0 00.07-.576A2.502 2.502 0 104.133 5.59a2.49 2.49 0 001.262-.343l6.955 5.413a5.2 5.2 0 00-.676 2.548c0 .97.267 1.878.73 2.656l-2.092 2.093a1.963 1.963 0 00-.576-.088 1.969 1.969 0 101.969 1.969 1.96 1.96 0 00-.088-.576l2.063-2.063a5.2 5.2 0 002.97.933 5.208 5.208 0 005.208-5.209 5.2 5.2 0 00-3.694-4.993zm-1.002 7.447a2.455 2.455 0 110-4.91 2.455 2.455 0 010 4.91z"/>
    </svg>
  ),
  quickbooks: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm4.615 16.154h-2.77V7.846H12v8.308H9.231V7.846c0-1.528 1.241-2.77 2.769-2.77h4.615v11.078z"/>
    </svg>
  ),
  jira: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
      <path d="M11.571 11.513H0a5.218 5.218 0 005.232 5.215h2.13v2.057A5.215 5.215 0 0012.575 24V12.518a1.005 1.005 0 00-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 005.215 5.214h2.129v2.058a5.218 5.218 0 005.215 5.214V6.758a1.001 1.001 0 00-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 005.215 5.215h2.129v2.057A5.215 5.215 0 0024 12.485V1.005A1.005 1.005 0 0023.013 0z"/>
    </svg>
  ),
  email: (
    <Mail className="w-8 h-8" />
  ),
  google_oauth: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  ),
  microsoft_oauth: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
      <path d="M11.4 24H0V12.6h11.4V24z" fill="#00A4EF"/>
      <path d="M24 24H12.6V12.6H24V24z" fill="#FFB900"/>
      <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#F25022"/>
      <path d="M24 11.4H12.6V0H24v11.4z" fill="#7FBA00"/>
    </svg>
  ),
};

const INTEGRATION_COLORS = {
  stripe: '#635bff',
  hubspot: '#ff7a59',
  quickbooks: '#2ca01c',
  jira: '#0052cc',
  email: '#6366f1',
  google_oauth: '#4285F4',
  microsoft_oauth: '#00A4EF',
};


export default function IntegrationsPage() {
  const { isSuperadmin, user } = useAuth();
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [testing, setTesting] = useState({});
  const [saving, setSaving] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [formData, setFormData] = useState({});
  const [logs, setLogs] = useState({});
  
  // Test email dialog state
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    if (!isSuperadmin) {
      navigate('/overview');
      return;
    }
    loadIntegrations();
  }, [isSuperadmin, navigate]); // loadIntegrations is stable
  
  useEffect(() => {
    // Pre-fill test email with current user's email
    if (user?.email) {
      setTestEmailAddress(user.email);
    }
  }, [user]);

  const loadIntegrations = async () => {
    try {
      const data = await superadminAPI.getIntegrations();
      setIntegrations(data.integrations || []);
      
      // Initialize form data from integrations
      const forms = {};
      data.integrations?.forEach(int => {
        forms[int.integration_type] = {
          config: int.config || {},
          credentials: {},
          is_enabled: int.is_enabled,
        };
      });
      setFormData(forms);
    } catch (error) {
      console.error('Failed to load integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      const result = await superadminAPI.sendTestEmail(testEmailAddress);
      
      if (result.success) {
        toast.success('Test email sent!', {
          description: `Check ${testEmailAddress} for the test email`,
        });
        setTestEmailDialogOpen(false);
        loadIntegrations(); // Refresh status
      } else {
        toast.error('Failed to send test email', {
          description: result.message,
          duration: 8000,
        });
        
        // Show fix suggestion if available
        if (result.fix) {
          toast.info('Suggested fix', {
            description: result.fix,
            duration: 10000,
          });
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('Failed to send test email', {
        description: errorMsg,
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleTestConnection = async (type) => {
    setTesting({ ...testing, [type]: true });
    try {
      const result = await superadminAPI.testIntegration(type);
      if (result.success) {
        toast.success(`${type} connection successful!`, {
          description: result.message,
        });
      } else {
        toast.error(`${type} connection failed`, {
          description: result.message,
        });
      }
      loadIntegrations();
    } catch (error) {
      toast.error(`Failed to test ${type} connection`, {
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setTesting({ ...testing, [type]: false });
    }
  };

  const handleSaveIntegration = async (type) => {
    setSaving({ ...saving, [type]: true });
    try {
      const data = formData[type];
      await superadminAPI.updateIntegration(type, data);
      toast.success(`${type} configuration saved`);
      loadIntegrations();
    } catch (error) {
      toast.error(`Failed to save ${type} configuration`, {
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setSaving({ ...saving, [type]: false });
    }
  };

  const handleToggleEnabled = async (type, enabled) => {
    setFormData(prev => ({
      ...prev,
      [type]: { ...prev[type], is_enabled: enabled }
    }));
    
    try {
      await superadminAPI.updateIntegration(type, { is_enabled: enabled });
      toast.success(`${type} ${enabled ? 'enabled' : 'disabled'}`);
      loadIntegrations();
    } catch (_error) {
      toast.error(`Failed to update ${type}`);
    }
  };

  const loadLogs = async (type) => {
    try {
      const data = await superadminAPI.getIntegrationLogs(type, 20);
      setLogs(prev => ({ ...prev, [type]: data.logs || [] }));
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  };

  const updateFormField = (type, section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [section]: {
          ...(prev[type]?.[section] || {}),
          [field]: value,
        }
      }
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getIntegration = (type) => integrations.find(i => i.integration_type === type) || {};

  if (!isSuperadmin) return null;

  const renderCredentialField = (type, field, placeholder, isSecret = true) => {
    const integration = getIntegration(type);
    const maskedValue = integration.credentials_masked?.[field] || '';
    const currentValue = formData[type]?.credentials?.[field] || '';
    const showValue = showSecrets[`${type}_${field}`];
    const isEnvField = (integration.env_fields || []).includes(field);
    
    return (
      <div className="space-y-1.5">
        <Label htmlFor={`${type}-${field}`} className="text-sm font-medium flex items-center gap-2">
          {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          {isEnvField && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
              ENV
            </Badge>
          )}
        </Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              id={`${type}-${field}`}
              type={isSecret && !showValue ? 'password' : 'text'}
              placeholder={isEnvField ? '(set via environment variable)' : (maskedValue || placeholder)}
              value={currentValue}
              onChange={(e) => updateFormField(type, 'credentials', field, e.target.value)}
              className={`pr-10 ${isEnvField ? 'border-emerald-500/30 bg-emerald-500/5' : ''}`}
              disabled={isEnvField}
              data-testid={`${type}-${field}-input`}
            />
            {isSecret && !isEnvField && (
              <button
                type="button"
                onClick={() => setShowSecrets(prev => ({ ...prev, [`${type}_${field}`]: !showValue }))}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderConfigField = (type, field, placeholder, description = null) => {
    const currentValue = formData[type]?.config?.[field] || '';
    
    return (
      <div className="space-y-1.5">
        <Label htmlFor={`${type}-config-${field}`} className="text-sm font-medium">
          {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </Label>
        <Input
          id={`${type}-config-${field}`}
          placeholder={placeholder}
          value={currentValue}
          onChange={(e) => updateFormField(type, 'config', field, e.target.value)}
          data-testid={`${type}-config-${field}-input`}
        />
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
    );
  };

  return (
    <PageShell maxWidth="7xl" data-testid="integrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/superadmin/tenants')}
            className="mr-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Plug className="h-6 w-6 text-primary" />
                Integrations
              </h1>
              <Badge className="bg-primary text-primary-foreground">
                <Shield className="h-3 w-3 mr-1" />
                Superadmin Only
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Connect external services for CRM, billing, support, email, and authentication
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="integrations-loading">
          <Skeleton className="h-20 w-full rounded-xl" />
          {[1, 2, 3].map((row) => (
            <Skeleton key={row} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview" data-testid="overview-tab" className="flex items-center gap-2">
              <Plug className="h-4 w-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="business" data-testid="business-tab" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Business</span>
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="email-main-tab" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="auth" data-testid="auth-tab" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              <span className="hidden sm:inline">Auth</span>
            </TabsTrigger>
            <TabsTrigger value="database" data-testid="database-tab" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">Database</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <IntegrationStatusPanel 
              integrations={integrations}
              icons={INTEGRATION_ICONS}
              colors={INTEGRATION_COLORS}
            />
          </TabsContent>

          {/* Business Apps Tab */}
          <TabsContent value="business" className="space-y-4">
            <IntegrationAccordion
              integrations={[
                { type: 'stripe', label: 'Stripe', icon: INTEGRATION_ICONS.stripe, color: INTEGRATION_COLORS.stripe },
                { type: 'hubspot', label: 'HubSpot', icon: INTEGRATION_ICONS.hubspot, color: INTEGRATION_COLORS.hubspot },
                { type: 'quickbooks', label: 'QuickBooks', icon: INTEGRATION_ICONS.quickbooks, color: INTEGRATION_COLORS.quickbooks },
                { type: 'jira', label: 'Jira', icon: INTEGRATION_ICONS.jira, color: INTEGRATION_COLORS.jira },
              ]}
              getIntegration={getIntegration}
              formData={formData}
              testing={testing}
              saving={saving}
              showSecrets={showSecrets}
              setShowSecrets={setShowSecrets}
              onTest={handleTestConnection}
              onSave={handleSaveIntegration}
              onToggle={handleToggleEnabled}
              onLoadLogs={loadLogs}
              logs={logs}
              copyToClipboard={copyToClipboard}
              updateFormField={updateFormField}
              renderCredentialField={renderCredentialField}
              renderConfigField={renderConfigField}
            />
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email" className="space-y-4">
            <EmailIntegrationPanel
              integration={getIntegration('email')}
              formData={formData}
              testing={testing}
              saving={saving}
              showSecrets={showSecrets}
              setShowSecrets={setShowSecrets}
              onTest={() => handleTestConnection('email')}
              onSave={() => handleSaveIntegration('email')}
              onToggle={(enabled) => handleToggleEnabled('email', enabled)}
              onLoadLogs={() => loadLogs('email')}
              logs={logs.email}
              copyToClipboard={copyToClipboard}
              updateFormField={updateFormField}
              renderCredentialField={renderCredentialField}
              renderConfigField={renderConfigField}
              onSendTestEmail={() => setTestEmailDialogOpen(true)}
            />
          </TabsContent>

          {/* Authentication Tab */}
          <TabsContent value="auth" className="space-y-4">
            <IntegrationAccordion
              integrations={[
                { type: 'google_oauth', label: 'Google OAuth', icon: INTEGRATION_ICONS.google_oauth, color: INTEGRATION_COLORS.google_oauth },
                { type: 'microsoft_oauth', label: 'Microsoft OAuth', icon: INTEGRATION_ICONS.microsoft_oauth, color: INTEGRATION_COLORS.microsoft_oauth },
              ]}
              getIntegration={getIntegration}
              formData={formData}
              testing={testing}
              saving={saving}
              showSecrets={showSecrets}
              setShowSecrets={setShowSecrets}
              onTest={handleTestConnection}
              onSave={handleSaveIntegration}
              onToggle={handleToggleEnabled}
              onLoadLogs={loadLogs}
              logs={logs}
              copyToClipboard={copyToClipboard}
              updateFormField={updateFormField}
              renderCredentialField={renderCredentialField}
              renderConfigField={renderConfigField}
              isOAuth={true}
            />
          </TabsContent>

          {/* Database Tab */}
          <TabsContent value="database" className="space-y-6">
            <GCPInfrastructurePanel />
          </TabsContent>
        </Tabs>
      )}
      
      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              Send Test Email
            </DialogTitle>
            <DialogDescription>
              Send a test email to verify your email integration is working correctly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="test-email-address">Recipient Email</Label>
              <Input
                id="test-email-address"
                type="email"
                placeholder="your@email.com"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                data-testid="test-email-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter the email address where you want to receive the test email.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendTestEmail} 
              disabled={sendingTestEmail || !testEmailAddress}
              data-testid="confirm-send-test-email"
            >
              {sendingTestEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Test Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

// Integration Accordion Component for collapsible panels
function IntegrationAccordion({
  integrations,
  getIntegration,
  formData,
  testing,
  saving,
  _showSecrets,
  _setShowSecrets,
  onTest,
  onSave,
  onToggle,
  onLoadLogs,
  logs,
  copyToClipboard,
  updateFormField,
  renderCredentialField,
  renderConfigField,
  _isOAuth = false,
}) {
  const [expandedItems, setExpandedItems] = useState([]);

  const getIntegrationContent = (type) => {
    switch (type) {
      case 'stripe':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'api_key', 'sk_test_xxxxxxxx')}
              {renderCredentialField(type, 'webhook_secret', 'whsec_xxxxxxxx')}
            </div>
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">Webhook Configuration</h4>
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <Webhook className="h-4 w-4 text-muted-foreground" />
                <code className="text-xs flex-1 overflow-x-auto">
                  {`${window.location.origin}/api/billing/stripe/webhook`}
                </code>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${window.location.origin}/api/billing/stripe/webhook`)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">Supported Events</h4>
              <div className="flex flex-wrap gap-2">
                {['checkout.session.completed', 'customer.subscription.updated', 'customer.subscription.deleted', 'invoice.payment_succeeded'].map((event) => (
                  <Badge key={event} variant="outline" className="text-xs">{event}</Badge>
                ))}
              </div>
            </div>
          </div>
        );
      case 'hubspot':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'api_key', 'pat-na1-xxxxxxxx')}
              {renderCredentialField(type, 'portal_id', '12345678', false)}
            </div>
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">Pipeline Configuration</h4>
              <div className="grid gap-4 sm:grid-cols-2">
                {renderConfigField(type, 'pipeline_id', 'default', 'HubSpot deals pipeline ID')}
                {renderConfigField(type, 'deal_stage_trial', 'qualifiedtobuy', 'Stage for trial tenants')}
              </div>
            </div>
          </div>
        );
      case 'quickbooks':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'client_id', 'ABxxxxxxxxxxxxxxxx')}
              {renderCredentialField(type, 'client_secret', 'xxxxxxxxxxxxxxxx')}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'realm_id', '123456789', false)}
              {renderCredentialField(type, 'access_token', 'eyJxxxxxxxx')}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'refresh_token', 'ABxxxxxxxx')}
            </div>
          </div>
        );
      case 'jira':
        return (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'domain', 'your-org.atlassian.net', false)}
              {renderCredentialField(type, 'email', 'admin@your-org.com', false)}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'api_token', 'ATATTxxxxxxxx')}
              {renderCredentialField(type, 'project_key', 'SUPPORT', false)}
            </div>
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">Automation Settings</h4>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">Auto-create on Limit Exceeded</p>
                    <p className="text-xs text-muted-foreground">Create ticket when tenant hits trial limits</p>
                  </div>
                  <Switch
                    checked={formData[type]?.config?.auto_create_on_limit_exceeded || false}
                    onCheckedChange={(checked) => updateFormField(type, 'config', 'auto_create_on_limit_exceeded', checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">Auto-create on Negative Feedback</p>
                    <p className="text-xs text-muted-foreground">Create ticket for low satisfaction scores</p>
                  </div>
                  <Switch
                    checked={formData[type]?.config?.auto_create_on_negative_feedback || false}
                    onCheckedChange={(checked) => updateFormField(type, 'config', 'auto_create_on_negative_feedback', checked)}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      case 'google_oauth':
        return (
          <div className="space-y-4">
            <div className="p-4 bg-secondary/30 rounded-lg flex items-start gap-3">
              <Users className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium">Google Social Login</p>
                <p className="text-xs text-muted-foreground">
                  Allow users to sign in with their Google accounts. Create credentials in the Google Cloud Console.
                </p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'client_id', 'xxxxxx.apps.googleusercontent.com', false)}
              {renderCredentialField(type, 'client_secret', 'GOCSPX-xxxxxxxxxx')}
            </div>
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">OAuth Settings</h4>
              <div className="grid gap-4 sm:grid-cols-2">
                {renderConfigField(type, 'redirect_uri', 'https://evainsights.vistry.ai/api/auth/google/callback', 'Authorized redirect URI')}
                {renderConfigField(type, 'allowed_domains', '', 'Restrict to domains (comma-separated)')}
              </div>
            </div>
            <div className="p-3 bg-secondary/50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <strong>Setup:</strong> Create OAuth credentials at{' '}
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Google Cloud Console <ExternalLink className="h-3 w-3 inline" />
                </a>
              </p>
            </div>
          </div>
        );
      case 'microsoft_oauth':
        return (
          <div className="space-y-4">
            <div className="p-4 bg-secondary/30 rounded-lg flex items-start gap-3">
              <Users className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium">Microsoft / Entra ID Social Login</p>
                <p className="text-xs text-muted-foreground">
                  Allow users to sign in with their Microsoft work or personal accounts.
                </p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'tenant_id', 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', false)}
              {renderCredentialField(type, 'client_id', 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', false)}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField(type, 'client_secret', 'xxxxxxxxxxxxxxxxxx')}
            </div>
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">OAuth Settings</h4>
              <div className="grid gap-4 sm:grid-cols-2">
                {renderConfigField(type, 'redirect_uri', 'https://evainsights.vistry.ai/api/auth/microsoft/callback', 'Redirect URI')}
                {renderConfigField(type, 'allowed_domains', '', 'Restrict to domains (comma-separated)')}
              </div>
            </div>
            <div className="p-3 bg-secondary/50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <strong>Setup:</strong> Register an application at{' '}
                <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Azure Portal <ExternalLink className="h-3 w-3 inline" />
                </a>
              </p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Accordion 
      type="multiple" 
      value={expandedItems} 
      onValueChange={setExpandedItems}
      className="space-y-3"
    >
      {integrations.map(({ type, label, icon, color }) => {
        const integration = getIntegration(type);

        return (
          <AccordionItem 
            key={type} 
            value={type}
            className="border rounded-lg bg-card overflow-hidden"
            data-testid={`accordion-${type}`}
          >
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-secondary/30">
              <div className="flex items-center gap-4 flex-1">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${color}15`, color }}
                >
                  {icon}
                </div>
                <div className="text-left flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{label}</span>
                    {integration.is_enabled ? (
                      <Badge className="bg-emerald-500/10 text-emerald-600 text-xs">Enabled</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">Disabled</Badge>
                    )}
                    {integration.config_source && (
                      <Badge variant="outline" className="text-xs">ENV</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{integration.description}</p>
                </div>
                <Switch
                  checked={formData[type]?.is_enabled || false}
                  onCheckedChange={(e) => {
                    e.stopPropagation();
                    onToggle(type, !formData[type]?.is_enabled);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  disabled={!!integration.config_source}
                  data-testid={`${type}-enable-toggle`}
                />
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="pt-4 border-t space-y-6">
                {/* Connection Status */}
                {integration.last_sync_at && (
                  <div className={`flex items-center gap-2 p-3 rounded-lg ${
                    integration.last_sync_status === 'success' 
                      ? 'bg-emerald-500/10 text-emerald-600' 
                      : 'bg-red-500/10 text-red-600'
                  }`}>
                    {integration.last_sync_status === 'success' ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <span className="text-sm">
                      Last tested: {new Date(integration.last_sync_at).toLocaleString()}
                    </span>
                  </div>
                )}

                {/* Dynamic content based on integration type */}
                {getIntegrationContent(type)}

                {/* Actions */}
                <div className="flex justify-between pt-4 border-t">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onTest(type)}
                      disabled={testing[type]}
                      data-testid={`${type}-test-btn`}
                    >
                      {testing[type] ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Test Connection
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onSave(type)}
                    disabled={saving[type]}
                    data-testid={`${type}-save-btn`}
                  >
                    {saving[type] && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save
                  </Button>
                </div>

                {/* Logs sub-accordion */}
                <Accordion type="single" collapsible className="border-t pt-4">
                  <AccordionItem value="logs" className="border-none">
                    <AccordionTrigger onClick={() => onLoadLogs(type)} className="py-2 hover:no-underline">
                      <span className="flex items-center gap-2 text-sm">
                        <FileText className="h-4 w-4" />
                        Activity Logs
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      {logs[type]?.length > 0 ? (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {logs[type].map((log, i) => (
                            <div 
                              key={i} 
                              className={`flex items-start gap-3 p-2 rounded text-sm ${
                                log.status === 'success' ? 'bg-emerald-500/5' : 
                                log.status === 'failed' ? 'bg-red-500/5' : 'bg-secondary/50'
                              }`}
                            >
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {new Date(log.created_at).toLocaleTimeString()}
                              </span>
                              <Badge variant="outline" className="text-xs">{log.direction}</Badge>
                              <span className="flex-1">{log.event_type}</span>
                              <Badge variant={log.status === 'success' ? 'default' : 'destructive'} className="text-xs">
                                {log.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground py-2 text-center">No activity logs yet</p>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

// Email Integration Panel Component
function EmailIntegrationPanel({
  integration,
  formData,
  testing,
  saving,
  _showSecrets,
  _setShowSecrets,
  onTest,
  onSave,
  onToggle,
  _onLoadLogs,
  _logs,
  _copyToClipboard,
  updateFormField,
  renderCredentialField,
  renderConfigField,
  onSendTestEmail,
}) {
  const EMAIL_PROVIDERS = [
    { id: 'exchange', name: 'Microsoft Exchange', description: 'Office 365 / Exchange Online' },
    { id: 'sendgrid', name: 'SendGrid', description: 'Twilio SendGrid API' },
    { id: 'smtp', name: 'Custom SMTP', description: 'Any SMTP server' },
    { id: 'ses', name: 'Amazon SES', description: 'AWS Simple Email Service' },
  ];

  return (
    <Card data-testid="email-integration-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-indigo-500/10">
            <Mail className="h-6 w-6 text-indigo-500" />
          </div>
          <div>
            <CardTitle className="flex items-center gap-2">
              Email Service
              {integration.is_enabled ? (
                <Badge className="bg-emerald-500/10 text-emerald-600">Enabled</Badge>
              ) : (
                <Badge variant="secondary">Disabled</Badge>
              )}
              {integration.config_source && (
                <Badge variant="outline" className="text-xs">ENV</Badge>
              )}
            </CardTitle>
            <CardDescription>
              {integration.description || 'Send transactional emails for invitations, password resets, and reports'}
            </CardDescription>
          </div>
        </div>
        <Switch
          checked={formData.email?.is_enabled || false}
          onCheckedChange={onToggle}
          disabled={!!integration.config_source}
          data-testid="email-enable-toggle"
        />
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection Status */}
        {integration.last_sync_at && (
          <div className={`flex items-center gap-2 p-3 rounded-lg ${
            integration.last_sync_status === 'success' 
              ? 'bg-emerald-500/10 text-emerald-600' 
              : 'bg-red-500/10 text-red-600'
          }`}>
            {integration.last_sync_status === 'success' ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            <span className="text-sm">
              Last tested: {new Date(integration.last_sync_at).toLocaleString()}
              {integration.last_error && ` - ${integration.last_error}`}
            </span>
          </div>
        )}

        {/* Email Not Enabled Warning */}
        {!integration.is_enabled && integration.is_configured && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-600">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">
              Email is configured but <strong>not enabled</strong>. Toggle the switch above to enable email sending.
            </span>
          </div>
        )}

        {/* Provider Selection */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Email Provider</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {EMAIL_PROVIDERS.map((provider) => (
              <button
                key={provider.id}
                type="button"
                onClick={() => updateFormField('email', 'config', 'provider', provider.id)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.email?.config?.provider === provider.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid={`email-provider-${provider.id}`}
              >
                <p className="font-medium text-sm">{provider.name}</p>
                <p className="text-xs text-muted-foreground">{provider.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Provider-specific configuration */}
        {formData.email?.config?.provider === 'exchange' && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="text-sm font-medium">Microsoft Exchange / Office 365</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField('email', 'tenant_id', 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', false)}
              {renderCredentialField('email', 'client_id', 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', false)}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField('email', 'client_secret', 'xxxxxxxxxxxxxxxxxx')}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderConfigField('email', 'sender_email', 'noreply@yourdomain.com', 'From address for outbound emails')}
              {renderConfigField('email', 'sender_name', 'Eva Insights', 'Display name for emails')}
            </div>
            <div className="p-3 bg-secondary/50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <strong>Setup:</strong> Register an app in Azure AD with Mail.Send permission.{' '}
                <a href="https://learn.microsoft.com/en-us/graph/auth-register-app-v2" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Learn more <ExternalLink className="h-3 w-3 inline" />
                </a>
              </p>
            </div>
          </div>
        )}

        {formData.email?.config?.provider === 'sendgrid' && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="text-sm font-medium">SendGrid Configuration</h4>
            <div className="grid gap-4">
              {renderCredentialField('email', 'api_key', 'SG.xxxxxxxxxxxxxxxxxx')}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderConfigField('email', 'sender_email', 'noreply@yourdomain.com', 'Verified sender email')}
              {renderConfigField('email', 'sender_name', 'Eva Insights', 'Display name')}
            </div>
          </div>
        )}

        {formData.email?.config?.provider === 'smtp' && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="text-sm font-medium">SMTP Server Configuration</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField('email', 'host', 'smtp.yourdomain.com', false)}
              {renderCredentialField('email', 'port', '587', false)}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField('email', 'username', 'user@yourdomain.com', false)}
              {renderCredentialField('email', 'password', 'password')}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderConfigField('email', 'sender_email', 'noreply@yourdomain.com', 'From address')}
              {renderConfigField('email', 'sender_name', 'Eva Insights', 'Display name')}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Use TLS</p>
                  <p className="text-xs text-muted-foreground">STARTTLS encryption</p>
                </div>
                <Switch
                  checked={formData.email?.config?.use_tls !== false}
                  onCheckedChange={(checked) => updateFormField('email', 'config', 'use_tls', checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Use SSL</p>
                  <p className="text-xs text-muted-foreground">Direct SSL connection</p>
                </div>
                <Switch
                  checked={formData.email?.config?.use_ssl || false}
                  onCheckedChange={(checked) => updateFormField('email', 'config', 'use_ssl', checked)}
                />
              </div>
            </div>
          </div>
        )}

        {formData.email?.config?.provider === 'ses' && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="text-sm font-medium">Amazon SES Configuration</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderCredentialField('email', 'access_key_id', 'AKIAXXXXXXXXXX')}
              {renderCredentialField('email', 'secret_access_key', 'xxxxxxxxxxxxxxxxxxxxxxxx')}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderConfigField('email', 'region', 'us-east-1', 'AWS region')}
              {renderConfigField('email', 'sender_email', 'noreply@yourdomain.com', 'Verified sender email')}
            </div>
          </div>
        )}

        {/* Email Use Cases */}
        <div className="pt-4 border-t">
          <h4 className="text-sm font-medium mb-3">Email Use Cases</h4>
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-sm font-medium">User Invitations</p>
              <p className="text-xs text-muted-foreground">Invite new team members</p>
            </div>
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-sm font-medium">Password Resets</p>
              <p className="text-xs text-muted-foreground">Secure reset links</p>
            </div>
            <div className="p-3 bg-secondary/30 rounded-lg">
              <p className="text-sm font-medium">Scheduled Reports</p>
              <p className="text-xs text-muted-foreground">Weekly/monthly digests</p>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between border-t pt-6">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onTest}
            disabled={testing.email}
            data-testid="email-test-btn"
          >
            {testing.email ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Test Connection
          </Button>
          <Button
            variant="outline"
            onClick={onSendTestEmail}
            disabled={!integration.is_enabled}
            data-testid="send-test-email-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Send Test Email
          </Button>
        </div>
        <Button
          onClick={onSave}
          disabled={saving.email}
          data-testid="email-save-btn"
        >
          {saving.email && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save Configuration
        </Button>
      </CardFooter>
    </Card>
  );
}
